import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { api, type User, type Membership } from './api'

interface AuthState {
  user: User | null
  memberships: Membership[]
  currentOrg: {
    orgId: string
    orgSlug: string
    role: 'admin' | 'approver' | 'special_guest' | 'guest'
    accountType: 'guest' | 'room_owner'
  } | null
  isLoading: boolean
  isAuthenticated: boolean
  
  // Actions
  login: (email: string, password: string) => Promise<void>
  register: (orgName: string, email: string, password: string, name: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  setCurrentOrg: (orgSlug: string) => Promise<void>
  refreshAuth: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      memberships: [],
      currentOrg: null,
      isLoading: true,
      isAuthenticated: false,

      login: async (email: string, password: string) => {
        const response = await api.auth.login({ email, password })
        
        const currentOrg = response.memberships[0] ? {
          orgId: response.memberships[0].orgId,
          orgSlug: response.memberships[0].orgSlug,
          role: response.memberships[0].role,
          accountType: response.memberships[0].accountType || 'guest' as const,
        } : null
        
        set({
          user: response.user,
          memberships: response.memberships,
          currentOrg,
          isAuthenticated: true,
        })
      },

      register: async (orgName: string, email: string, password: string, name: string) => {
        const response = await api.auth.register({ orgName, email, password, name })
        
        const currentOrg = response.organisation ? {
          orgId: response.organisation.id,
          orgSlug: response.organisation.slug,
          role: 'admin' as const,
          accountType: 'room_owner' as const,
        } : null
        
        set({
          user: response.user,
          memberships: response.memberships || [{
            orgId: response.organisation!.id,
            orgSlug: response.organisation!.slug,
            orgName: response.organisation!.name,
            role: 'admin',
            accountType: 'room_owner',
          }],
          currentOrg,
          isAuthenticated: true,
        })
      },

      logout: async () => {
        await api.auth.logout()
        set({
          user: null,
          memberships: [],
          currentOrg: null,
          isAuthenticated: false,
        })
      },

      checkAuth: async () => {
        try {
          set({ isLoading: true })
          const response = await api.auth.me()
          
          set({
            user: response.user,
            memberships: response.memberships,
            currentOrg: response.currentOrg || null,
            isAuthenticated: true,
            isLoading: false,
          })
        } catch {
          set({
            user: null,
            memberships: [],
            currentOrg: null,
            isAuthenticated: false,
            isLoading: false,
          })
        }
      },

      setCurrentOrg: async (orgSlug: string) => {
        const membership = get().memberships.find(m => m.orgSlug === orgSlug)
        if (membership) {
          // Call API to update the cookie
          try {
            await api.auth.switchOrg(orgSlug)
          } catch (e) {
            console.error('Failed to switch org:', e)
          }
          
          set({
            currentOrg: {
              orgId: membership.orgId,
              orgSlug: membership.orgSlug,
              role: membership.role,
              accountType: membership.accountType || 'guest',
            },
          })
        }
      },
      
      refreshAuth: async () => {
        try {
          const response = await api.auth.me()
          set({
            user: response.user,
            memberships: response.memberships,
            currentOrg: response.currentOrg || null,
            isAuthenticated: true,
          })
        } catch {
          // Ignore - keep current state
        }
      },
    }),
    {
      name: 'roomsi-auth',
      partialize: (state) => ({
        user: state.user,
        memberships: state.memberships,
        currentOrg: state.currentOrg,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
